import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import {
  PROPERTY_TYPES, QUICK_DATE_RANGES, getCmaReportType, quickRange,
} from "../cmaReportTypes";

const inputClass =
  "w-full min-w-0 px-3 py-2 rounded-md border border-gray-200 bg-white text-sm text-gray-900 "
  + "focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40";

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-xs font-bold text-white mb-1">{children}</label>
);

const Range = ({ id, label, value, onChange, fromKey, toKey, type = "number", suffix, prefix }) => (
  <div>
    <Label htmlFor={`${id}-from`}>{label}</Label>
    <div className="flex items-center gap-2">
      <div className="relative flex-1 min-w-0">
        {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">{prefix}</span>}
        <input
          id={`${id}-from`}
          type={type}
          min={type === "number" ? 0 : undefined}
          aria-label={`${label} from`}
          value={value[fromKey] ?? ""}
          onChange={(e) => onChange({ [fromKey]: e.target.value })}
          className={`${inputClass} ${prefix ? "pl-6" : ""} ${suffix ? "pr-10" : ""}`}
        />
        {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">{suffix}</span>}
      </div>
      <span className="text-xs font-bold text-white">to</span>
      <div className="relative flex-1 min-w-0">
        {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">{prefix}</span>}
        <input
          type={type}
          min={type === "number" ? 0 : undefined}
          aria-label={`${label} to`}
          value={value[toKey] ?? ""}
          onChange={(e) => onChange({ [toKey]: e.target.value })}
          className={`${inputClass} ${prefix ? "pl-6" : ""} ${suffix ? "pr-10" : ""}`}
        />
        {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">{suffix}</span>}
      </div>
    </div>
  </div>
);

/**
 * Search Options, the block under the target address on every Property Data
 * report page. Controlled: `value` is the criteria object, `onChange` gets a
 * partial update.
 */
const CmaSearchOptions = ({ reportType, value, onChange }) => {
  const type = getCmaReportType(reportType);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div>
      <h3 className="text-lg text-white mb-3">Search Options</h3>

      {type.statement && (
        <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 text-sm font-bold text-white">
          <legend className="sr-only">Property location</legend>
          <span>Property Location:</span>
          {[["metro", "Metropolitan Melbourne"], ["regional", "Outside Metropolitan Melbourne"]].map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="pps-location"
                checked={(value.location || "metro") === key}
                onChange={() => set({ location: key })}
              />
              {label}
            </label>
          ))}
        </fieldset>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4">
        <div className="space-y-4">
          <div className="relative">
            <div className="flex items-center justify-between">
              <Label htmlFor="cma-date-from">{type.dateLabel}</Label>
              <button
                type="button"
                onClick={() => setShowQuick((v) => !v)}
                className="text-xs font-semibold text-white/90 hover:text-white inline-flex items-center gap-1 mb-1"
              >
                Quick set date range <ChevronDown className="w-3 h-3" />
              </button>
            </div>
            {showQuick && (
              <div className="absolute right-0 top-6 z-20 w-44 bg-white rounded-md shadow-lg border border-gray-100 py-1">
                {QUICK_DATE_RANGES.map((range) => (
                  <button
                    key={range.label}
                    type="button"
                    onClick={() => { set(quickRange(range)); setShowQuick(false); }}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                id="cma-date-from"
                type="date"
                aria-label={`${type.dateLabel} from`}
                value={value.date_from || ""}
                onChange={(e) => set({ date_from: e.target.value })}
                className={inputClass}
              />
              <span className="text-xs font-bold text-white">to</span>
              <input
                type="date"
                aria-label={`${type.dateLabel} to`}
                value={value.date_to || ""}
                onChange={(e) => set({ date_to: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <Range
            id="cma-price"
            label={type.rental ? "Rent" : "Price"}
            value={value}
            onChange={set}
            fromKey="price_min"
            toKey="price_max"
            prefix="$"
            suffix={type.rental ? "pw" : undefined}
          />

          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 text-sm font-bold text-white cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(value.include_surrounding)}
                onChange={(e) => set({
                  include_surrounding: e.target.checked,
                  radius_km: e.target.checked ? (value.radius_km || 2) : value.radius_km,
                })}
              />
              Include Surrounding:
            </label>
            <div className="relative w-28">
              <input
                type="number"
                min="0"
                step="0.5"
                aria-label="Surrounding distance in km"
                value={value.radius_km ?? ""}
                disabled={!value.include_surrounding}
                onChange={(e) => set({ radius_km: e.target.value })}
                className={`${inputClass} pr-9 disabled:opacity-60`}
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">km</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="cma-property-type">Property Type</Label>
            <select
              id="cma-property-type"
              value={value.property_type || ""}
              onChange={(e) => set({ property_type: e.target.value })}
              className={inputClass}
            >
              <option value="">All Property Types</option>
              {PROPERTY_TYPES.map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Range id="cma-beds" label="Bedrooms" value={value} onChange={set} fromKey="beds_min" toKey="beds_max" />
            {type.rental && (
              <Range id="cma-baths" label="Bathrooms" value={value} onChange={set} fromKey="baths_min" toKey="baths_max" />
            )}
          </div>
          {type.rental && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Range id="cma-cars" label="Carparks" value={value} onChange={set} fromKey="cars_min" toKey="cars_max" />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
          className="inline-flex items-center gap-1 text-base text-white hover:underline"
        >
          {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Advanced Options
        </button>
      </div>

      {showAdvanced && (
        <div className="mt-3 -mx-4 sm:-mx-5 px-4 sm:px-5 py-4 bg-gray-100 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="[&_label]:text-gray-700 [&_span.font-bold]:text-gray-700">
            <Range id="cma-land" label="Land Area" value={value} onChange={set} fromKey="land_min" toKey="land_max" suffix="sqm." />
          </div>
          {!type.rental && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&_label]:text-gray-700 [&_span.font-bold]:text-gray-700">
              <Range id="cma-baths" label="Bathrooms" value={value} onChange={set} fromKey="baths_min" toKey="baths_max" />
              <Range id="cma-cars" label="Carparks" value={value} onChange={set} fromKey="cars_min" toKey="cars_max" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CmaSearchOptions;
